import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { DifmPopup } from '@yola/webapp-plugins';
import segment from 'src/js/modules/analytics/segment';
import useFeatureFlags from 'src/js/modules/feature-flags/hooks/use-feature-flags';
import dialogs from '../../dialogs';
import useOnceWhen from '../../common/hooks/use-once-when';
import user from '../../user';
import { hasGoldSubscription } from '../verifiers/has-gold-subscription';
import isDifmOrderSubmitted from '../verifiers/is-difm-order-submitted';

const {
  track,
  constants: { events },
} = segment;

const ROUTES_TO_HIDE_DIFM_POPUP = ['/upgrade-ecommerce', '/domain-name-search', '/upgrade'];
const DIFM_POPUP = 'DIFM_POPUP';

const shouldShowPopup = (storageKey) => {
  if (!storageKey) return false;

  const nextDateToShow = localStorage.getItem(storageKey);
  if (!nextDateToShow) return true;

  return nextDateToShow <= new Date();
};

const DifmPopupContainer = () => {
  const { id: userId } = useSelector(user.selectors.getUserData, shallowEqual);

  const difmPopupKey = userId ? `${DIFM_POPUP}_${userId}` : null;
  const isInitiallyVisible = shouldShowPopup(difmPopupKey);

  const [isVisible, setIsVisible] = useState(isInitiallyVisible);
  const location = useLocation();
  const activeDialog = useSelector(dialogs.selectors.getDialog);
  const dispatch = useDispatch();
  const [featureFlags] = useFeatureFlags(['nn_difm_promo']);

  const { nn_difm_promo: nnDifmPromoFeatureFlagEnabled } = featureFlags;
  const isGold = hasGoldSubscription();
  const isOrderSubmitted = isDifmOrderSubmitted();

  const shouldRenderDifmPopup =
    nnDifmPromoFeatureFlagEnabled &&
    !isGold &&
    !isOrderSubmitted &&
    isVisible &&
    !activeDialog.type &&
    !ROUTES_TO_HIDE_DIFM_POPUP.some((route) => location.pathname.startsWith(route));

  const markDifmPopupAsClosed = useCallback(() => {
    const inOneMonth = new Date().setMonth(new Date().getMonth() + 1);

    localStorage.setItem(difmPopupKey, inOneMonth);
  }, [difmPopupKey]);

  const handleClose = useCallback(() => {
    markDifmPopupAsClosed();

    track(events.DIFM_PROMOTION_CLOSE_CLICKED, {
      businessCategory: null,
      siteId: null,
      templateBuildSlug: null,
    });

    setIsVisible(false);
  }, [markDifmPopupAsClosed]);

  const handleNext = useCallback(() => {
    markDifmPopupAsClosed();

    track(events.DIFM_PROMOTION_LEARNMORE_CLICKED, {
      businessCategory: null,
      siteId: null,
      templateBuildSlug: null,
    });

    setIsVisible(false);
    dispatch(dialogs.actions.show(dialogs.dialogTypes.DIFM_UPSELL_DIALOG));
  }, [dispatch, markDifmPopupAsClosed]);

  useOnceWhen(shouldRenderDifmPopup, () => {
    track(events.DIFM_PROMOTION_DISPLAYED, {
      businessCategory: null,
      siteId: null,
      templateBuildSlug: null,
    });
  });

  useEffect(() => {
    setIsVisible(isInitiallyVisible);
  }, [isInitiallyVisible]);

  if (!shouldRenderDifmPopup) return null;

  return <DifmPopup onNext={handleNext} onClose={handleClose} />;
};

export default DifmPopupContainer;
